import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, generatePath } from 'react-router-dom'
import PropTypes from 'prop-types'

import {
  Button,
  Checkbox,
  Modal,
  notification,
  Table,
  Typography,
  Tag
} from 'antd'
import * as Icons from '@ant-design/icons'
import moment from 'moment'

import { ScreenSpin } from 'admin/lib/ScreenSpin'
import { Queries } from 'admin/lib/Api'
import {
  hasCertExpired,
  isCertExpiringSoon
} from 'admin/lib/Certificates/CertificateExpiry'
import { CertificateTag } from 'admin/lib/Certificates/CertificateTag'

import * as ROUTES from 'admin/routes'
import { useCompanyDelete } from 'admin/lib/Api/queries'

export function CompaniesList({ selectedRowIds, setSelectedRowIds }) {
  const companies = Queries.useCompanies()
  const user = Queries.useUserSelf()
  const [companyFilterOptions, setCompanyFilterOptions] = useState([])
  const [filters, setFilters] = useState({})
  const [sorter, setSorter] = useState({})
  const [deleteCompany, setDeleteCompany] = useState(null)

  const deleteApiCall = useCompanyDelete()

  useEffect(() => {
    // Load saved filters and sorter from local storage
    const savedFilters =
      JSON.parse(localStorage.getItem('companiesFilters')) || {}
    const savedSorter =
      JSON.parse(localStorage.getItem('companiesSorter')) || {}
    setFilters(savedFilters)
    setSorter(savedSorter)
  }, [])

  useEffect(() => {
    // Save filters and sorter to local storage whenever they change
    localStorage.setItem('companiesFilters', JSON.stringify(filters))
    localStorage.setItem('companiesSorter', JSON.stringify(sorter))
  }, [filters, sorter])

  const allCompanyFilterOptions = useMemo(() => {
    const activeNameFilters =
      filters.name && filters.name.length > 0
        ? filters.name.map(company => {
            return {
              text: company,
              value: company
            }
          })
        : []

    const allCompanies =
      companies.data && companies.data.length > 0
        ? companies.data.map(company => {
            return {
              text: company.name,
              value: company.name
            }
          })
        : []

    return [
      ...activeNameFilters,
      ...allCompanies.filter(company => {
        return !activeNameFilters.some(
          activeCompany => activeCompany.value === company.value
        )
      })
    ]
  }, [companies.data, filters])

  useEffect(() => {
    setCompanyFilterOptions(allCompanyFilterOptions)
  }, [allCompanyFilterOptions, companies.data])

  const certificateFilterOptions = [
    { text: 'No certificates', value: 'none' },
    { text: 'Has certificate(s)', value: 'some' },
    { text: 'Expired', value: 'expired' },
    { text: 'Expiring soon', value: 'expiring' }
  ]

  const columns = [
    {
      title: '',
      key: 'selected',
      dataIndex: 'companyId',
      width: 116,
      render: companyId => {
        return (
          <Checkbox
            checked={selectedRowIds[companyId]}
            onChange={e =>
              setSelectedRowIds(list => ({
                ...list,
                [companyId]: e.target.checked
              }))
            }
            style={{ whiteSpace: 'nowrap' }}
          >
            {selectedRowIds[companyId] ? 'Selected' : 'Select'}
          </Checkbox>
        )
      }
    },
    {
      title: 'Company No.',
      dataIndex: 'companyNumber',
      width: 144,
      sorter: (a, b) => a.companyNumber.localeCompare(b.companyNumber),
      sortOrder: sorter.field === 'companyNumber' && sorter.order
    },
    {
      title: 'Company',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sorter.field === 'name' && sorter.order,
      filters: companyFilterOptions,
      filterSearch: true,
      filteredValue: filters.name || null,
      onFilter: (value, record) => record.name.startsWith(value)
    },
    {
      title: 'Address',
      dataIndex: 'address'
    },
    {
      title: 'Certificates',
      dataIndex: 'companyCertificates',
      filters: certificateFilterOptions,
      filteredValue: filters.companyCertificates || null,
      onFilter: (value, record) => {
        switch (value) {
          case 'none':
            return (
              !record.companyCertificates ||
              record.companyCertificates.length < 1
            )

          case 'some':
            return (
              record.companyCertificates &&
              record.companyCertificates.length > 0
            )

          case 'expired':
            return (
              record.companyCertificates &&
              record.companyCertificates.some(
                cert => cert.expirationDate && hasCertExpired(cert)
              )
            )
          case 'expiring':
            return (
              record.companyCertificates &&
              record.companyCertificates.some(
                cert =>
                  cert.expirationDate &&
                  !hasCertExpired(cert) &&
                  isCertExpiringSoon(cert)
              )
            )
        }
      },
      render: certificates =>
        certificates?.map(cert =>
          cert.certificationScheme ? (
            <CertificateTag key={cert.certificateId} cert={cert} />
          ) : (
            <Tag key={cert.certificateId} color="red">
              (Incomplete Certificate)
            </Tag>
          )
        )
    },
    {
      title: 'Created At',
      dataIndex: 'createdAtDate',
      width: 128,
      render: createdAt =>
        createdAt ? new Date(createdAt).toLocaleString() : '',
      sorter: (a, b) =>
        moment(a.createdAtDate).valueOf() - moment(b.createdAtDate).valueOf(),
      sortOrder: sorter.field === 'createdAtDate' && sorter.order
    },
    {
      title: '',
      key: 'edit',
      width: 64,
      render: company => (
        <Typography.Text>
          <Link
            to={generatePath(ROUTES.COMPANIES.EDIT, {
              companyId: company.companyId
            })}
          >
            Edit
          </Link>
        </Typography.Text>
      )
    }
  ]

  // Show Delete button to Super Users
  if (user.data && user.data.isSuper) {
    columns.push({
      title: '',
      key: 'delete',
      dataIndex: 'companyId',
      width: 64,
      render: companyId => (
        <Button
          type="text"
          tabIndex={-1}
          danger
          icon={<Icons.DeleteOutlined />}
          value={companyId}
          onClick={e => handleRemoveButtonClick(companyId)}
        />
      )
    })
  }

  const handleTableChange = (pagination, filters, sorter, extra) => {
    const newFilters = { ...filters }
    if (newFilters) {
      for (const key in newFilters) {
        if (Array.isArray(newFilters[key])) {
          newFilters[key].sort() // Sorts alphabetically by default
        }
      }
    }
    setFilters(newFilters)
    setSorter({ field: sorter.field, order: sorter.order })
  }

  const handleRemoveButtonClick = companyId => {
    const companyName = companies.data.filter(company => {
      return company.companyId === companyId
    })[0].name
    setDeleteCompany({ companyId, companyName })
  }

  const handleConfirmDelete = useCallback(async () => {
    const deleteCompanyId = deleteCompany.companyId
    setDeleteCompany(null)

    try {
      await deleteApiCall.mutateAsync({ companyId: deleteCompanyId })

      notification.success({
        message: 'Company successfully removed! 🙌'
      })
    } catch (e) {
      notification.error({
        message:
          'Delete failed 😦. ' +
          e?.response.status +
          ': ' +
          String(e?.response.data.Message)
      })
    }
  }, [deleteApiCall, deleteCompany])

  return (
    <>
      {!companies.isFetched && <ScreenSpin />}

      {companies.isError && <div>{String(companies.error)}</div>}

      {companies.isFetched && companies.data && (
        <Table
          columns={columns}
          dataSource={companies.data}
          onChange={handleTableChange}
          rowKey="companyId"
          pagination={{
            defaultPageSize: 100,
            showSizeChanger: true,
            pageSizeOptions: [50, 100, 200, 300, 1000]
          }}
          virtual={true}
          scroll={{ x: window.innerWidth - 64, y: window.innerHeight - 224 }}
        />
      )}

      <Modal
        title="Are you sure you want to remove this company?"
        open={deleteCompany}
        onCancel={e => setDeleteCompany(null)}
        onOk={handleConfirmDelete}
      >
        <p>
          <Typography.Text>
            {deleteCompany ? deleteCompany.companyName : ''}
          </Typography.Text>
        </p>
        <p>
          <Typography.Text>
            This operation cannot be undone. Click 'OK' to delete this company.
          </Typography.Text>
        </p>{' '}
      </Modal>
    </>
  )
}

CompaniesList.propTypes = {
  selectedRowIds: PropTypes.object.isRequired,
  setSelectedRowIds: PropTypes.func.isRequired
}
